import React, { useState, useRef } from 'react';
import { makeStyles, tokens, mergeClasses } from '@fluentui/react-components';
import CopilotIcon from '../../../assets/office/ribbon/Copilot.svg';

const useStyles = makeStyles({
  container: {
    position: 'fixed',
    top: '156px', // Header (48px) + Ribbon (84px) + 24px gap
    right: '24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '8px',
    zIndex: 1000,
    opacity: 1,
    transform: 'scale(1)',
    transition: 'opacity 0.2s ease-in, transform 0.2s ease-in',
  },
  
  // Hidden state for animation
  containerHidden: {
    opacity: 0,
    transform: 'scale(0.9)',
    pointerEvents: 'none',
    transition: 'opacity 0.15s ease-out, transform 0.15s ease-out',
  },
  
  // Adjust position when Copilot pane is open
  containerWithCopilot: {
    right: '400px', // Account for Copilot pane width (360px) + padding
  },
  
  // DAB button container with shadow
  dabButton: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '16px',
    padding: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '0px',
    boxShadow: '0px 8px 16px 0px rgba(0,0,0,0.14), 0px 0px 2px 0px rgba(0,0,0,0.12)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  
  // Inner button (subtle style)
  innerButton: {
    backgroundColor: 'transparent',
    borderRadius: '4px',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    border: 'none',
    cursor: 'pointer',
  },
  
  // Copilot icon container
  copilotIcon: {
    width: '24px',
    height: '24px',
    position: 'relative',
  },
  
  // Individual icon shapes
  iconShape1: {
    position: 'absolute',
    width: '9.7px',
    height: '8.325px',
    left: 'calc(50% + 3.56px)',
    top: 'calc(50% - 5.84px)',
    transform: 'translate(-50%, -50%)',
  },
  iconShape2: {
    position: 'absolute',
    width: '9.524px',
    height: '8.58px',
    left: 'calc(50% - 3.39px)',
    top: 'calc(50% + 5.71px)',
    transform: 'translate(-50%, -50%)',
  },
  iconShape3: {
    position: 'absolute',
    width: '13.751px',
    height: '14.44px',
    left: 'calc(50% - 4.12px)',
    top: 'calc(50% - 2.78px)',
    transform: 'translate(-50%, -50%)',
  },
  iconShape4: {
    position: 'absolute',
    width: '13.751px',
    height: '14.44px',
    left: 'calc(50% + 4.12px)',
    top: 'calc(50% + 2.78px)',
    transform: 'translate(-50%, -50%)',
  },
  
  // Pills container (shown on hover)
  pillsContainer: {
    display: 'none',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '8px',
  },
  
  pillsContainerVisible: {
    display: 'flex',
  },
  
  // Individual pill - matching TopOfDoc pill style (without icon)
  pill: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    padding: '10px 14px 10px 12px',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '12px',
    cursor: 'pointer',
    opacity: 0,
    transform: 'scale(0.9)',
    animationName: {
      from: { opacity: 0, transform: 'scale(0.9)' },
      to: { opacity: 1, transform: 'scale(1)' },
    },
    animationDuration: '0.15s',
    animationFillMode: 'forwards',
    animationTimingFunction: 'ease-out',
    transition: 'all 0.1s ease',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      boxShadow: tokens.shadow4,
      border: '1px solid transparent',
    },
  },
  
  pillText: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    color: tokens.colorNeutralForeground2,
    whiteSpace: 'nowrap',
    fontFamily: '"Segoe UI", sans-serif',
  },
});

interface FloatingCopilotButtonStyleCProps {
  isVisible: boolean;
  onPillClick?: (pillText: string) => void;
  onDabClick?: () => void;
}

export const FloatingCopilotButtonStyleC: React.FC<FloatingCopilotButtonStyleCProps> = ({ 
  isVisible,
  onPillClick,
  onDabClick
}) => {
  const styles = useStyles();
  const [isHovered, setIsHovered] = useState(false);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = () => {
    // Clear any pending hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    // Delay hiding pills to allow user to move mouse to pills
    hideTimeoutRef.current = setTimeout(() => {
      setIsHovered(false);
    }, 100);
  };

  const pills = [
    'Summarize this document',
    'Prepare me for a meeting',
    'Get writing suggestions',
    'More',
  ];

  const handlePillClick = (pillText: string) => {
    if (pillText === 'More') {
      // TODO: Show more options
      console.log('More options clicked');
    } else {
      onPillClick?.(pillText);
    }
  };

  return (
    <div 
      className={mergeClasses(styles.container, !isVisible && styles.containerHidden)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* DAB Button */}
      <div 
        className={styles.dabButton} 
        onClick={onDabClick}
      >
        <div className={styles.innerButton}>
          <img src={CopilotIcon} alt="Copilot" style={{ width: '24px', height: '24px' }} />
        </div>
      </div>
      
      {/* Pills - shown on hover */}
      <div className={mergeClasses(styles.pillsContainer, isHovered && styles.pillsContainerVisible)}>
        {pills.map((pill, index) => (
          <div 
            key={index}
            className={styles.pill}
            style={{ animationDelay: `${index * 0.05}s` }}
            onClick={() => handlePillClick(pill)}
          >
            <span className={styles.pillText}>{pill}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FloatingCopilotButtonStyleC;
